import { intersection } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useLoggedInUserPermissions,
  useLoggedInUserRoles,
} from '~/components/Auth';
import { useUserSettings } from '~/components/UserSettings';
import { useCompanyPreferences } from '~/data/companyPreferences/useCompanyPreferences';
import { AdminWorkspaceRoles } from '~/data/user/role';
import {
  MyWorkspaceWidgetNames,
  getAvailableWidgets,
} from '~/scenes/MyWorkspace/components/Widgets/data';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import {
  CompanyWorkspaceSettingsKey,
  MyWorkspaceUserSettingsKey,
  WorkspaceId_NO_WORKSPACE,
  WorkspaceSettings,
} from '~/scenes/MyWorkspace/userSettings';
import { isRoleHRAdmin } from '~/services/Permissions';

export interface WorkspacesSettings {
  workspaces: Array<WorkspaceSettings>;
  activeWorkspaceId?: string;
  useWorkspaceAsHomepage?: boolean;
}

export const filterWorkspaceWidgets = (
  workspaces: WorkspaceSettings[],
  availableWidgets: MyWorkspaceWidgetNames[],
) => {
  return workspaces.map((workspace) => ({
    ...workspace,
    widgets: workspace.widgets.filter(({ widget }) => {
      return availableWidgets.includes(widget);
    }),
  }));
};

export const filterWorkspacesByWidgets = (
  workspaces: WorkspaceSettings[],
  availableWidgets: MyWorkspaceWidgetNames[],
): Array<WorkspaceSettings> => {
  const filterWorkspace = (workspace: WorkspaceSettings) =>
    workspace.widgets.filter(
      (w) => !(availableWidgets as string[]).includes(w.id),
    ).length === 0;

  return workspaces.filter((w) => filterWorkspace(w));
};

export const hasWorkspaceAdminAccess = (
  workspaceType: WorkspaceType,
  roles: AdminWorkspaceRoles[],
): boolean => {
  return workspaceType === WorkspaceType.Company && isRoleHRAdmin(roles);
};

export const filterWorkspacesByRolesRestrictions = (
  workspaces: WorkspaceSettings[],
  roles: AdminWorkspaceRoles[],
): WorkspaceSettings[] => {
  return workspaces.filter((workspace) => {
    const workspaceType: WorkspaceType = workspace.type as WorkspaceType;

    // Unrestricted or is not Company workspace
    if (
      workspace.restrictToRoles.length === 0 ||
      workspaceType !== WorkspaceType.Company
    ) {
      return true;
    }

    // Exception: HR_ADMIN can always edit Company Workspaces
    if (hasWorkspaceAdminAccess(workspaceType, roles)) {
      return true;
    }

    // Check for restricted access
    return intersection(workspace.restrictToRoles, roles).length > 0;
  });
};

export const filterAdminOnlyWorkspaces = (
  workspaces: WorkspaceSettings[],
  roles: AdminWorkspaceRoles[],
): WorkspaceSettings[] => {
  return workspaces.filter((workspace) => {
    return (
      workspace.restrictToRoles.length > 0 &&
      intersection(workspace.restrictToRoles, roles).length === 0
    );
  });
};

export const prepWorkspacesDefaults = (
  workspaces: WorkspaceSettings[],
  defaultWorkspaceType: WorkspaceType,
): WorkspaceSettings[] => {
  return workspaces.map((workspace) => ({
    ...workspace,
    type: workspace.type || defaultWorkspaceType,
    restrictToRoles: workspace.restrictToRoles || [],
  }));
};

export const filterUniqueWorkspaces = (workspaces: WorkspaceSettings[]) =>
  workspaces.filter(
    (workspace, index, self) =>
      self.findIndex((v) => v.id === workspace.id) === index,
  );

export const getGeneralWorkspacesSettingsResolver = (
  roles: string[],
  enabledModules: string[],
  workspacesType: WorkspaceType,
) => {
  const availableWidgets = getAvailableWidgets(roles, enabledModules);

  const noWorkspaces: WorkspacesSettings = {
    workspaces: [],
  };

  return (
    remoteWorkspacesPreferences?: Partial<WorkspacesSettings>,
  ): WorkspacesSettings => {
    try {
      const otherWorkspaceSettings =
        workspacesType === WorkspaceType.Personal &&
        !!remoteWorkspacesPreferences?.activeWorkspaceId
          ? {
              activeWorkspaceId: remoteWorkspacesPreferences.activeWorkspaceId,
              useWorkspaceAsHomepage:
                remoteWorkspacesPreferences.useWorkspaceAsHomepage,
            }
          : {};

      if (!remoteWorkspacesPreferences?.workspaces?.length) {
        return { ...noWorkspaces, ...otherWorkspaceSettings };
      }

      return {
        workspaces: filterUniqueWorkspaces(
          prepWorkspacesDefaults(
            filterWorkspaceWidgets(
              filterWorkspacesByRolesRestrictions(
                remoteWorkspacesPreferences.workspaces,
                roles as AdminWorkspaceRoles[],
              ),
              availableWidgets,
            ),
            workspacesType,
          ),
        ),
        ...otherWorkspaceSettings,
      };
    } catch {
      return noWorkspaces;
    }
  };
};

export interface UseAllWorkspacesOutput {
  workspaces: Array<WorkspaceSettings>;
  activeWorkspace: WorkspaceSettings;
  adminOnlyWorkspaces: Array<WorkspaceSettings>;
  activeWorkspaceRole: AdminWorkspaceRoles;
  hasData: boolean;
}

export const useWorkspaceSettings = (): UseAllWorkspacesOutput => {
  const { t } = useTranslation();

  const userRoles = useLoggedInUserRoles();
  const workspaceRoles = useMemo((): AdminWorkspaceRoles[] => {
    const ur = Object.values(AdminWorkspaceRoles).filter((r) =>
      userRoles.includes(r),
    );
    return ur.includes(AdminWorkspaceRoles.EMPLOYEE)
      ? ur
      : [...ur, AdminWorkspaceRoles.EMPLOYEE];
  }, [userRoles]);

  const { enabledModules } = useLoggedInUserPermissions();

  // Company Workspaces
  const resolveCompanyWorkspaceSettings = useMemo(() => {
    return getGeneralWorkspacesSettingsResolver(
      workspaceRoles,
      enabledModules,
      WorkspaceType.Company,
    );
  }, [workspaceRoles, enabledModules]);

  const { companyWorkspaces, hasDataCompanyWorkspaces } = useCompanyPreferences(
    resolveCompanyWorkspaceSettings,
    CompanyWorkspaceSettingsKey,
  );

  // Personal Workspaces
  const resolveMyWorkspaceUserSettings = useMemo(() => {
    return getGeneralWorkspacesSettingsResolver(
      workspaceRoles,
      enabledModules,
      WorkspaceType.Personal,
    );
  }, [workspaceRoles, enabledModules]);

  const { personalWorkspaces, hasDataPersonalWorkspaces } = useUserSettings(
    resolveMyWorkspaceUserSettings,
    MyWorkspaceUserSettingsKey,
  );

  // Combined workspaces
  const workspaces: Array<WorkspaceSettings> = useMemo(() => {
    return filterUniqueWorkspaces([
      ...companyWorkspaces.workspaces,
      ...personalWorkspaces.workspaces,
    ]);
  }, [companyWorkspaces, personalWorkspaces]);

  const activeWorkspace: WorkspaceSettings = useMemo(() => {
    if (workspaces.length <= 0) {
      return {
        id: WorkspaceId_NO_WORKSPACE,
        name: t('createWorkspace'),
        widgets: [],
        type: WorkspaceType.Default,
        restrictToRoles: [],
      };
    }

    return (
      workspaces.find(({ id }) => {
        return id === personalWorkspaces.activeWorkspaceId;
      }) || workspaces[0]
    );
  }, [t, workspaces, personalWorkspaces.activeWorkspaceId]);

  const adminOnlyWorkspaces: WorkspaceSettings[] = useMemo(() => {
    return filterAdminOnlyWorkspaces(
      companyWorkspaces.workspaces,
      workspaceRoles,
    );
  }, [companyWorkspaces, workspaceRoles]);

  return {
    workspaces,
    activeWorkspace,
    adminOnlyWorkspaces,
    activeWorkspaceRole: activeWorkspace?.restrictToRoles?.[0],
    hasData: hasDataCompanyWorkspaces && hasDataPersonalWorkspaces,
  };
};
